// src/components/study/ScreeningVisitCard.js
//
// "Upload Protocol" -> the study's SCREENING VISIT checklist.
//
// The upload and the read reuse the Protocol -> SoA pipeline exactly as the
// SourceX Protocols page does (same uploadAndCreateProtocol, same
// extractProtocolSoa, same protocol documents), so a protocol uploaded here is
// the same protocol SourceX sees, and vice versa. What is new is what happens
// once the SoA is read: the screening column's rows become "Needs X" boxes,
// listed UNTICKED — the checklist is only what must be done at the hospital,
// usually a short subset — the admin ticks what applies, adds anything the
// table left out, and confirms. A checklist can also be built entirely by hand
// with no protocol, and confirming with nothing ticked is a valid answer
// ("nothing needs to be done at the hospital"). The confirmed list is saved on
// the study and shown to coordinators on every patient marked eligible for it
// (PatientDetailPage).
//
// Two modes:
//   confirmed  — `study.screeningVisit.status === "confirmed"`: the list, read
//                only, with Edit for admins.
//   draft      — anything else: the checklist being built from the latest
//                extraction, or the upload prompt if there is none.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  Upload,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Plus,
  Pencil,
  X,
  FileText,
  RefreshCw,
  ExternalLink,
} from "lucide-react";
import StudyCard from "./StudyCard";
import { auth } from "../../firebase";
import {
  uploadAndCreateProtocol,
  startExtraction,
  listenProtocols,
  listenExtraction,
} from "../../services/protocolExtractionService";
import { PROTOCOL_STATUS } from "../../services/protocolTypes";
import { saveScreeningVisit, markScreeningChecked } from "../../services/studyScreeningService";
import {
  deriveScreeningItems,
  manualItem,
  buildConfirmedScreeningVisit,
  hasConfirmedScreening,
  diffConfirmedAgainstSoa,
} from "../../services/screeningVisit";
import { PROTOCOL_EXTRACTION_ENABLED, PROTOCOL_COMPARE_ENABLED } from "../../config/featureFlags";
import { listenVersions, addVersion, logProtocolEvent } from "../../services/protocolVersionService";
import { newVersionQuestion, currentVersion, effectiveVersion, currentProtocolInfo, describeCurrentProtocol } from "../../services/protocolVersions";
import NewVersionDialog from "./NewVersionDialog";
import AiHelperNotice from "../AiHelperNotice";
import { formatTimestamp } from "../../utils/dateUtils";
import { useDialog } from "../sourcex/useDialog";

const AI_WARNING =
  "This checklist was read from the protocol by AI, and AI can make mistakes — it can miss, misread or mislabel items.\n\n" +
  "Check every item against the protocol's Schedule of Assessments before confirming. Don't rely on it as-is.";

const btnPrimary =
  "group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed";
const btnGhost =
  "inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs font-medium text-slate-200 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed";
const inputCls =
  "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50";

const ms = (p) => p?.uploadedAt?.toMillis?.() ?? p?.createdAt?.toMillis?.() ?? 0;

/** Newest approved protocol, else the newest of any status — same order findProtocolUrl uses. */
const pickProtocol = (protocols) => {
  if (!protocols?.length) return null;
  const newestFirst = (a, b) => ms(b) - ms(a);
  const approved = protocols.filter((p) => p.status === PROTOCOL_STATUS.APPROVED).sort(newestFirst);
  return approved[0] || [...protocols].sort(newestFirst)[0];
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

/**
 * "by someone@… on 12 Sep 2026, 14:05" for a protocol, or "" when neither fact
 * is known.
 *
 * `uploadedAt` is a serverTimestamp, which is null for the moment between the
 * local write and the server's acknowledgement — a protocol uploaded seconds
 * ago has no time yet and must read "just now", never "Invalid Date". Documents
 * written before the field existed fall back to `createdAt`.
 */
const describeUpload = (protocol) => {
  if (!protocol) return "";
  const when = formatTimestamp(protocol.uploadedAt || protocol.createdAt);
  const who = protocol.createdBy || "";
  // Neither fact on the document — how protocols predating these fields read.
  // Say nothing rather than inventing a time.
  if (!who && !when) return "";
  const whenPart = when ? `on ${when}` : "just now";
  return who ? `by ${who} ${whenPart}` : whenPart;
};

const ScreeningVisitCard = ({ study, canEdit, onStudyPatched, onDirtyChange }) => {
  // TWO different study keys, and they are not interchangeable.
  //
  // `studyId` is the Firestore document id (`createStudy` uses addDoc, so it is
  // an auto-id). It addresses the study document itself, so it is what saving
  // the confirmed checklist and linking back to /study/:id must use.
  //
  // `protocolStudyKey` is the study's STUDY_ID field — the sponsor's number.
  // SourceX keys EVERYTHING on it: SourceXHome routes /sourcex/protocols/:id
  // with STUDY_ID (SourceXHome.js:422), and so do the template, eCRF and join
  // steps. All protocol data in production lives under that key.
  //
  // Using the doc id here wrote protocols to a path SourceX never reads. It
  // looked fine on the 63 studies whose doc id happens to equal their STUDY_ID
  // and silently diverged on the other 40. Falls back to the doc id only when a
  // study has no STUDY_ID at all, since the path helper rejects a blank id.
  const studyId = study?.id;
  const protocolStudyKey = String(study?.STUDY_ID || "").trim() || studyId;
  const confirmed = hasConfirmedScreening(study) ? study.screeningVisit : null;

  const [protocols, setProtocols] = useState([]);
  const [extraction, setExtraction] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null); // { items, visitLabel, visitIds, guessedVisit, protocolId }
  const [newItem, setNewItem] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Set while asking "there is already a protocol — upload another anyway?".
  // Only ever raised by pressing Upload, because that is the only moment the
  // question means anything.
  const [confirmReplace, setConfirmReplace] = useState(false);
  const fileRef = useRef(null);
  // Named apart from this card's own confirm() (the save).
  const { alert: showDialogAlert, confirm: askConfirm, dialog } = useDialog();
  // The protocol the "AI can make mistakes" pop-up was last shown for, so it
  // appears once per protocol per page visit rather than on every re-render.
  const aiWarnedForRef = useRef(null);
  // True once the admin has changed the draft (ticked, renamed, added,
  // removed, switched protocol) and not yet confirmed or cancelled. Drives the
  // "you have unsaved changes" warnings so hand-typed work isn't lost.
  const [dirty, setDirty] = useState(false);
  // Editing a checklist confirmed from an EARLIER protocol keeps that
  // checklist. Switching it to the newer protocol is an explicit, confirmed
  // choice ("Rebuild from the new protocol"), never a side effect of Edit.
  const [useNewProtocol, setUseNewProtocol] = useState(false);
  // Protocol versions (flag PROTOCOL_COMPARE_ENABLED): an upload on a study that
  // already has a protocol asks "Is this a new version?". Yes = the next version
  // (stored and compared, not read here); No = a re-upload, read as before.
  const [versions, setVersions] = useState([]);
  const [askVersion, setAskVersion] = useState(null); // { file, current, next }
  const [versionNote, setVersionNote] = useState("");
  // The protocol this card just uploaded to READ (a "No" answer), so it isn't
  // hidden by the rule below while it starts being read.
  const [ownUploadId, setOwnUploadId] = useState(null);
  // "Upload new protocol" answered before the file is picked: true = a new
  // version, false = the same version again. null = not asked.
  const [preAnswer, setPreAnswer] = useState(null);
  // "Rebuild from Version N": the protocol the checklist is being rebuilt from.
  const [rebuildTargetId, setRebuildTargetId] = useState(null);

  // With protocol versions on, a protocol that was only stored and never read
  // (a new version, uploaded with no reading on purpose) isn't this card's
  // protocol: the card keeps the one it had, with no "Reading…" and no Rebuild.
  const protocol = useMemo(() => {
    if (!PROTOCOL_COMPARE_ENABLED) return pickProtocol(protocols);
    const target = rebuildTargetId && protocols.find((p) => p.id === rebuildTargetId);
    if (target) return target;
    // The checklist's own protocol stays on the card; a newer version is the
    // "might be out of date" warning, never a silent swap.
    const own = confirmed?.protocolId && protocols.find((p) => p.id === confirmed.protocolId && p.status !== PROTOCOL_STATUS.UPLOADED);
    if (own && versions.length) return own;
    const read = protocols.filter((p) => p.status !== PROTOCOL_STATUS.UPLOADED || p.id === ownUploadId);
    return pickProtocol(read.length ? read : protocols);
  }, [protocols, ownUploadId, rebuildTargetId, confirmed?.protocolId, versions.length]);

  // Tell the page (for its Back buttons) and the browser (reload / close tab).
  useEffect(() => {
    onDirtyChange && onDirtyChange(dirty);
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      // Browsers show their own wording; a returned string is still required.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  useEffect(() => {
    if (!protocolStudyKey) return undefined;
    return listenProtocols(protocolStudyKey, setProtocols, () => {});
  }, [protocolStudyKey]);

  useEffect(() => {
    if (!PROTOCOL_COMPARE_ENABLED || !canEdit || !protocolStudyKey) return undefined;
    return listenVersions(protocolStudyKey, setVersions, () => setVersions([]));
  }, [protocolStudyKey, canEdit]);

  useEffect(() => {
    setExtraction(null);
    if (!protocolStudyKey || !protocol?.id) return undefined;
    if (protocol.status !== PROTOCOL_STATUS.NEEDS_REVIEW && protocol.status !== PROTOCOL_STATUS.APPROVED) {
      return undefined;
    }
    return listenExtraction(protocolStudyKey, protocol.id, setExtraction, () => {});
  }, [protocolStudyKey, protocol?.id, protocol?.status]);

  // Build (or rebuild) the draft whenever we are in draft mode and a fresh
  // extraction lands. Manual additions the admin already typed are kept.
  useEffect(() => {
    if (confirmed && !editing) return;
    if (!extraction) return;
    // Editing a checklist confirmed from an earlier protocol: keep that
    // checklist exactly as it is. The newer protocol is only used after the
    // admin chooses it (rebuildFromCurrentProtocol).
    if (confirmed && editing && confirmed.protocolId && confirmed.protocolId !== protocol?.id && !useNewProtocol) return;
    const derived = deriveScreeningItems(extraction);
    setDraft((prev) => {
      // Hand-added items always survive a rebuild — whichever protocol the
      // draft came from, or none (a checklist started by hand). They were typed
      // by a person and belong to no protocol.
      const manual = prev ? (prev.items || []).filter((i) => i.source === "manual") : [];
      // When editing a confirmed list, start from what was confirmed: keep its
      // selections, and offer the SoA rows it left out as unticked.
      const prior = new Map((confirmed?.items || []).map((i) => [i.id, i]));
      const fromSameProtocol = confirmed && editing && confirmed.protocolId === protocol?.id;
      const items = derived.items.map((i) =>
        fromSameProtocol
          ? { ...i, selected: prior.has(i.id), label: prior.get(i.id)?.label || i.label }
          : i
      );
      const confirmedManual =
        confirmed && editing ? (confirmed.items || []).filter((i) => i.source === "manual").map((i) => ({ ...i, selected: true })) : [];
      return {
        ...derived,
        protocolId: protocol?.id || "",
        items: [...items, ...confirmedManual, ...manual.filter((m) => !confirmedManual.some((c) => c.id === m.id))],
      };
    });
    // A fresh AI-read list is on screen: warn once for this protocol. Not when
    // re-opening a checklist already confirmed from this same protocol.
    const reopeningConfirmed = confirmed && confirmed.protocolId === protocol?.id;
    if (canEdit && protocol?.id && !reopeningConfirmed && aiWarnedForRef.current !== protocol.id) {
      aiWarnedForRef.current = protocol.id;
      showDialogAlert(AI_WARNING, { title: "AI can make mistakes", confirmLabel: "I understand" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraction, editing, protocol?.id, useNewProtocol]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !protocolStudyKey) return;
    if (PROTOCOL_COMPARE_ENABLED && canEdit) {
      const q = newVersionQuestion(versions, protocols);
      if (preAnswer != null) {
        const yes = preAnswer;
        setPreAnswer(null);
        setAskVersion({ file, current: q.current, next: q.next });
        await uploadProtocolFile(file, yes, q.next);
        return;
      }
      if (q.ask) {
        setAskVersion({ file, current: q.current, next: q.next });
        return;
      }
    }
    await uploadProtocolFile(file, false);
  };

  const uploadProtocolFile = async (file, asNewVersion, nextNumber) => {
    const next = nextNumber || askVersion?.next;
    const firstProtocol = PROTOCOL_COMPARE_ENABLED && canEdit && protocols.length === 0;
    setAskVersion(null);
    setError("");
    setVersionNote("");
    setBusy(true);
    try {
      const { protocolId } = await uploadAndCreateProtocol(protocolStudyKey, file);
      setConfirmReplace(false);
      if (asNewVersion) {
        // A new version is stored and compared against the one before; it is not
        // read here, and this card keeps the checklist it has.
        setVersionNote(`Saved as Version ${next}. Nothing else changes here; compare it under Protocol versions.`);
        setOwnUploadId(null);
        addVersion({ studyKey: protocolStudyKey, protocolId, source: "screening-card" }).catch((err) => setError(err.message || "Couldn't add the version."));
        return;
      }
      setOwnUploadId(protocolId);
      // A study's first protocol, uploaded by an admin here, is Version 1.
      if (firstProtocol) addVersion({ studyKey: protocolStudyKey, protocolId, source: "screening-card" }).catch(() => {});
      // Fire-and-forget; the protocol listener tracks status from here.
      startExtraction(protocolStudyKey, protocolId).catch((err) => setError(err.message || "Extraction could not be started."));
      // Only drop into the editor when there is nothing to lose. A CONFIRMED
      // checklist stays on screen: the new protocol announces itself with the
      // notice below, and rebuilding from it is a deliberate click. Entering
      // edit mode here used to hide the confirmed list and hand back a draft
      // with every row re-ticked, which read as if the work had been thrown
      // away (it had not — it was still saved, and Cancel restored it).
      if (!confirmed) setEditing(true);
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const retry = async () => {
    if (!protocol) return;
    setError("");
    try {
      await startExtraction(protocolStudyKey, protocol.id);
    } catch (err) {
      setError(err.message || "Retry failed.");
    }
  };

  // Editing a confirmed list starts from that list, so the admin sees what
  // they confirmed even before (or without) the extraction arriving; the
  // extraction effect above then folds in any SoA rows they had unticked.
  const beginEdit = () => {
    if (confirmed) {
      setDraft({
        items: (confirmed.items || []).map((i) => ({ ...i, selected: true })),
        visitLabel: confirmed.visitLabel || "",
        visitIds: confirmed.visitIds || [],
        guessedVisit: Boolean(confirmed.guessedVisit),
        protocolId: confirmed.protocolId || protocol?.id || "",
      });
    }
    setUseNewProtocol(false);
    setDirty(false);
    setEditing(true);
  };

  // Start the checklist again from the NEWER protocol the card is showing.
  // Always asked first and spelled out: the SoA rows on the current checklist
  // are replaced by the new protocol's (which start unticked); hand-added items
  // are kept. Nothing is saved until Confirm, and Cancel puts the saved
  // checklist back untouched.
  const rebuildFromCurrentProtocol = async (target) => {
    // `target`: the protocol being switched to, when it isn't the one on the card yet.
    const named = target && target.fileName ? target : protocol;
    const saved = confirmed?.items || [];
    const fromSoa = saved.filter((i) => i.source !== "manual").length;
    const byHand = saved.length - fromSoa;
    const ok = await askConfirm(
      `Use the new protocol${named?.fileName ? ` (${named.fileName})` : ""} for this checklist?\n\n` +
        `The ${fromSoa} item${fromSoa === 1 ? "" : "s"} currently on the checklist from the earlier protocol will be replaced ` +
        "by the new protocol's list, which starts unticked." +
        (byHand ? ` The ${byHand} item${byHand === 1 ? "" : "s"} you added by hand will be kept.` : "") +
        "\n\nNothing is saved until you click Confirm — Cancel puts the current checklist back.",
      { title: "Replace the checklist with the new protocol?", confirmLabel: "Yes, use the new protocol", cancelLabel: "Keep the current checklist" }
    );
    if (!ok) return false;
    setDraft(null);
    setUseNewProtocol(true);
    setDirty(true);
    setEditing(true);
    return true;
  };

  const toggle = (id) => {
    setDirty(true);
    setDraft((d) => ({ ...d, items: d.items.map((i) => (i.id === id ? { ...i, selected: !i.selected } : i)) }));
  };

  const relabel = (id, label) => {
    setDirty(true);
    setDraft((d) => ({ ...d, items: d.items.map((i) => (i.id === id ? { ...i, label } : i)) }));
  };

  const remove = (id) => {
    setDirty(true);
    setDraft((d) => ({ ...d, items: d.items.filter((i) => i.id !== id) }));
  };

  const addManual = () => {
    const item = manualItem(newItem);
    if (!item) return;
    setDirty(true);
    setDraft((d) => ({ ...(d || { items: [], visitLabel: "", visitIds: [], guessedVisit: false, protocolId: protocol?.id || "" }), items: [...(d?.items || []), item] }));
    setNewItem("");
  };

  // Start a checklist by hand — no protocol needed. Same editor as the
  // extracted draft; a protocol uploaded later keeps these items.
  const beginManual = () => {
    setDraft({ items: [], visitLabel: "", visitIds: [], guessedVisit: false, protocolId: "" });
    setDirty(false);
    setEditing(true);
  };

  const cancelEdit = async () => {
    if (dirty) {
      const ok = await askConfirm("You have changes to this checklist that haven't been confirmed. Discard them?", {
        title: "Discard unsaved changes?",
        confirmLabel: "Discard changes",
        cancelLabel: "Keep editing",
        tone: "danger",
      });
      if (!ok) return;
    }
    setEditing(false);
    setUseNewProtocol(false);
    setRebuildTargetId(null);
    setDirty(false);
    // A hand-started draft with no protocol and nothing saved has nothing to
    // return to — drop it so the card goes back to its empty state.
    if (!confirmed && (!protocol || protocol.status === PROTOCOL_STATUS.FAILED)) setDraft(null);
  };

  const confirm = async () => {
    if (!draft || !studyId) return;
    setError("");
    const payload = buildConfirmedScreeningVisit({
      ...draft,
      protocolId: draft.protocolId || protocol?.id || "",
      confirmedBy: auth.currentUser?.email || "",
    });
    // Nothing ticked is a valid answer, but make sure it's deliberate.
    if (!payload.items.length) {
      const ok = await askConfirm(
        "Nothing is ticked. Save this checklist as \"nothing needs to be done at the hospital\"?",
        { title: "Save an empty checklist?", confirmLabel: "Save as nothing needed" }
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      await saveScreeningVisit(studyId, payload);
      onStudyPatched && onStudyPatched({ screeningVisit: payload });
      setEditing(false);
      setUseNewProtocol(false);
      setDirty(false);
    } catch (err) {
      setError(err.message || "Could not save the checklist.");
    } finally {
      setBusy(false);
    }
  };

  const status = protocol?.status;
  const reading = status === PROTOCOL_STATUS.UPLOADED || status === PROTOCOL_STATUS.PROCESSING;
  const failed = status === PROTOCOL_STATUS.FAILED;
  const readyForDraft = extraction && (status === PROTOCOL_STATUS.NEEDS_REVIEW || status === PROTOCOL_STATUS.APPROVED);
  const showConfirmed = confirmed && !editing;
  const confirmedItems = confirmed?.items || [];
  const selectedCount = (draft?.items || []).filter((i) => i.selected !== false).length;

  // The SourceX route is keyed by STUDY_ID like the rest of SourceX; the `from`
  // state carries the DOC id, because that is what /study/:id resolves.
  const reviewHref =
    PROTOCOL_EXTRACTION_ENABLED && protocol?.id
      ? `/sourcex/protocols/${protocolStudyKey}/${protocol.id}`
      : "";

  const uploadedBlurb = describeUpload(protocol);

  // Pressing Upload when a protocol is already here asks first, rather than
  // silently starting a second extraction of (usually) the same PDF.
  //
  // This used to be an unconditional banner offering "use the existing one or
  // upload a new one". That asked a question with no alternative behind it:
  // the card ALREADY uses the protocol it finds, so anyone who had just
  // uploaded one and come back was being asked whether to use the thing they
  // had personally just put there. The warning belongs at the upload, which is
  // the only moment there is a decision to make.
  const askBeforeReplacing = confirmReplace && Boolean(protocol);
  const currentInfo = PROTOCOL_COMPARE_ENABLED ? currentProtocolInfo(versions, protocols) : null;
  const nextNumber = PROTOCOL_COMPARE_ENABLED ? newVersionQuestion(versions, protocols).next : null;

  // "Rebuild from Version N": rebuild from the current version, reading it
  // first if nobody has (a click, never automatic; it uses AI).
  const rebuildFromVersion = async () => {
    const mine = protocols.filter((p) => effectiveVersion(p, versions) === curVersion.version);
    const read = mine.find((p) => p.status === PROTOCOL_STATUS.NEEDS_REVIEW || p.status === PROTOCOL_STATUS.APPROVED);
    if (read) {
      setRebuildTargetId(read.id);
      // Same confirm and draft as "Rebuild from the new protocol".
      if (await rebuildFromCurrentProtocol(read)) {
        logProtocolEvent(protocolStudyKey, { type: "checklist_rebuild", version: curVersion.version, protocolId: read.id });
      } else {
        setRebuildTargetId(null);
      }
      return;
    }
    const target = mine.find((p) => p.status !== PROTOCOL_STATUS.FAILED) || mine[0];
    if (!target) return;
    if (target.status === PROTOCOL_STATUS.PROCESSING) {
      showDialogAlert(`Version ${curVersion.version} is being read now. Come back in a few minutes to rebuild from it.`, { title: "Still reading" });
      return;
    }
    const ok = await askConfirm(
      `Version ${curVersion.version} hasn't been read yet. Reading it uses AI and takes a few minutes. The checklist doesn't change until you confirm a new one.`,
      { title: `Read Version ${curVersion.version}?`, confirmLabel: "Read it" }
    );
    if (!ok) return;
    setOwnUploadId(target.id);
    setRebuildTargetId(target.id);
    logProtocolEvent(protocolStudyKey, { type: "protocol_read", version: curVersion.version, protocolId: target.id, detail: target.fileName || "" });
    startExtraction(protocolStudyKey, target.id).catch((err) => setError(err.message || "Reading could not be started."));
  };

  // "I've checked, it's still right": the checklist stays as it is, recorded
  // as checked against the current version (who and when), and the warning goes.
  const checkedStillRight = async () => {
    const by = String(auth.currentUser?.email || "").toLowerCase();
    try {
      await markScreeningChecked(studyId, { version: curVersion.version, protocolId: curVersion.protocolId, by });
      onStudyPatched &&
        onStudyPatched({ screeningVisit: { ...confirmed, checkedVersion: curVersion.version, checkedProtocolId: curVersion.protocolId, checkedBy: by, checkedAt: new Date().toISOString() } });
      logProtocolEvent(protocolStudyKey, { type: "checklist_checked", version: curVersion.version, protocolId: curVersion.protocolId });
    } catch (err) {
      setError(err.message || "Couldn't save that.");
    }
  };

  // The confirmed checklist came from one protocol; the card is now showing a
  // different one. Nothing is broken — the checklist is a saved snapshot — but
  // silently describing another protocol above it would be misleading.
  // Protocol versions on: a newer VERSION than the one this checklist was made
  // from (or last checked against) gets its own warning below, and replaces this one.
  const curVersion = PROTOCOL_COMPARE_ENABLED ? currentVersion(versions) : null;
  const checklistVersion =
    curVersion && confirmed
      ? confirmed.checkedVersion || effectiveVersion(protocols.find((p) => p.id === confirmed.protocolId) || { id: confirmed.protocolId }, versions)
      : null;
  const outdated = !!(curVersion && curVersion.version >= 2 && checklistVersion != null && checklistVersion < curVersion.version);
  const supersededBy =
    !curVersion && confirmed && protocol?.id && confirmed.protocolId && confirmed.protocolId !== protocol.id
      ? protocol
      : null;
  const confirmedFromName =
    (protocols.find((p) => p.id === confirmed?.protocolId) || {}).fileName || "";

  // Same protocol, but its schedule has been corrected since this checklist was
  // confirmed — typically someone editing the SoA in SourceX. The checklist is
  // a snapshot and deliberately does not follow, so without this the two just
  // disagree in silence. Skipped when `supersededBy` is set, because a
  // different protocol is a different message and one notice is enough.
  const drift = useMemo(
    () =>
      confirmed && !supersededBy && extraction && confirmed.protocolId === protocol?.id
        ? diffConfirmedAgainstSoa(confirmed, extraction)
        : null,
    [confirmed, supersededBy, extraction, protocol?.id]
  );

  return (
    <StudyCard
      title="Screening Visit"
      subtitle={
        showConfirmed
          ? `${
              confirmedItems.length
                ? `${confirmedItems.length} requirement${confirmedItems.length === 1 ? "" : "s"}`
                : "Nothing needed at the hospital"
            } · confirmed ${fmtWhen(confirmed.confirmedAt)}${confirmed.confirmedBy ? ` by ${confirmed.confirmedBy}` : ""}`
          : "What the protocol's Schedule of Assessments requires at screening"
      }
      icon={<ClipboardCheck className="w-3.5 h-3.5 text-purple-300" />}
      actions={
        canEdit && (
          <>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleUpload} />
            {showConfirmed && (
              <button type="button" onClick={beginEdit} className={btnGhost}>
                <Pencil className="w-3.5 h-3.5" />
                Edit checklist
              </button>
            )}
            {!confirmed && !editing && (!protocol || failed) && (
              <button type="button" onClick={beginManual} className={btnGhost} disabled={busy}>
                <Plus className="w-3.5 h-3.5" />
                Build checklist manually
              </button>
            )}
            <button
              type="button"
              // With a protocol already here, ask before starting a second
              // extraction; with none, go straight to the picker — there is
              // nothing to weigh up.
              onClick={() => (protocol ? setConfirmReplace(true) : fileRef.current?.click())}
              disabled={busy || reading}
              className={protocol ? btnGhost : btnPrimary}
              title={protocol ? "Upload a newer protocol PDF" : "Upload the study protocol PDF"}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 relative" />}
              <span className="relative">{protocol ? "Upload new protocol" : "Upload Protocol"}</span>
            </button>
          </>
        )
      }
    >
      {PROTOCOL_COMPARE_ENABLED && protocol && (draft || confirmed) && (
        <AiHelperNotice compact className="mb-2">The checklist items were read from the protocol by AI.</AiHelperNotice>
      )}
      {versionNote && (
        <div role="status" className="mb-2 text-[12px] text-slate-300">
          {versionNote}
        </div>
      )}
      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Upload pressed while a protocol is already here. */}
      {askBeforeReplacing && (
        <div className="mb-3 rounded-xl border border-sky-400/30 bg-sky-500/15 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-sky-300" />
            {currentInfo ? (
              <p className="text-xs text-sky-100 leading-snug">
                A protocol for this study was already uploaded: {describeCurrentProtocol(currentInfo)}. You can use that one,
                or upload a new version.
              </p>
            ) : (
              <p className="text-xs text-sky-100 leading-snug">
                A protocol for this study was already uploaded
                {uploadedBlurb ? ` ${uploadedBlurb}` : ""}
                {protocol?.fileName ? ` (${protocol.fileName})` : ""}, and this checklist is built
                from it. Uploading another reads the whole schedule again.
              </p>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => setConfirmReplace(false)} className={btnPrimary}>
              <CheckCircle2 className="w-3.5 h-3.5 relative" />
              <span className="relative">{currentInfo ? `Use Version ${currentInfo.version}` : "Keep the existing one"}</span>
            </button>
            {currentInfo ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmReplace(false);
                    setPreAnswer(true);
                    fileRef.current?.click();
                  }}
                  disabled={busy || reading}
                  className={btnGhost}
                >
                  <Upload className="w-3.5 h-3.5" />
                  Upload a new version (Version {nextNumber})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmReplace(false);
                    setPreAnswer(false);
                    fileRef.current?.click();
                  }}
                  disabled={busy || reading}
                  className={btnGhost}
                >
                  Upload Version {currentInfo.version} again
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setConfirmReplace(false);
                  fileRef.current?.click();
                }}
                disabled={busy || reading}
                className={btnGhost}
              >
                <Upload className="w-3.5 h-3.5" />
                Upload another anyway
              </button>
            )}
          </div>
        </div>
      )}

      {/* A newer protocol VERSION than this checklist was made from (versions on). */}
      {outdated && (
        <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-500/15 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" />
            <p className="text-xs text-amber-100 leading-snug">
              Version {curVersion.version} of the protocol was uploaded. This checklist was made from Version {checklistVersion || 1} and
              might be out of date.
            </p>
          </div>
          {canEdit && (
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={rebuildFromVersion} disabled={busy || reading} className={btnGhost}>
                <RefreshCw className="w-3.5 h-3.5" />
                Rebuild from Version {curVersion.version}
              </button>
              <button type="button" onClick={checkedStillRight} disabled={busy} className={btnGhost}>
                <CheckCircle2 className="w-3.5 h-3.5" />
                I&apos;ve checked, it&apos;s still right
              </button>
            </div>
          )}
        </div>
      )}
      {curVersion && confirmed && confirmed.checkedVersion && !outdated && (
        <p className="mb-2 text-[11px] text-slate-400">
          Checked against Version {confirmed.checkedVersion}
          {confirmed.checkedBy ? ` by ${confirmed.checkedBy}` : ""}
          {confirmed.checkedAt ? ` on ${new Date(confirmed.checkedAt).toLocaleDateString(undefined, { dateStyle: "medium" })}` : ""}.
        </p>
      )}

      {/* A newer protocol arrived after this checklist was confirmed. */}
      {supersededBy && (
        <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-500/15 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" />
            <p className="text-xs text-amber-100 leading-snug">
              A newer protocol was uploaded
              {describeUpload(supersededBy) ? ` ${describeUpload(supersededBy)}` : ""}. The
              checklist below is the one you confirmed
              {confirmedFromName ? ` from “${confirmedFromName}”` : ""} and has not changed.
            </p>
          </div>
          {canEdit && !useNewProtocol && (
            <button type="button" onClick={rebuildFromCurrentProtocol} className={`${btnGhost} mt-2`}>
              <RefreshCw className="w-3.5 h-3.5" />
              Rebuild from the new protocol
            </button>
          )}
          {useNewProtocol && editing && (
            <p className="mt-2 text-[11px] text-amber-200">
              You're now building the checklist from the new protocol. Nothing is saved until you click
              Confirm — Cancel puts the current checklist back.
            </p>
          )}
          {!useNewProtocol && editing && (
            <p className="mt-2 text-[11px] text-amber-200">
              You're editing the checklist you confirmed from the earlier protocol. To switch to the new
              protocol, use “Rebuild from the new protocol”.
            </p>
          )}
        </div>
      )}

      {/* The schedule this checklist came from has been edited since. */}
      {drift?.changed && showConfirmed && (
        <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-500/15 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" />
            <div className="min-w-0 text-xs text-amber-100 leading-snug">
              <p>
                The protocol's schedule has been edited since this checklist was confirmed, so the
                two no longer agree.
              </p>
              <ul className="mt-1.5 space-y-0.5 text-[11px] text-amber-200">
                {drift.removed.map((name) => (
                  <li key={`rm-${name}`}>
                    <span className="text-amber-200">“{name}”</span> is on the checklist but is no
                    longer marked at screening.
                  </li>
                ))}
                {drift.renamed.map((r) => (
                  <li key={`rn-${r.from}`}>
                    <span className="text-amber-200">“{r.from}”</span> is now called{" "}
                    <span className="text-amber-200">“{r.to}”</span>.
                  </li>
                ))}
                {drift.visitChanged && (
                  <li>
                    The screening column is now{" "}
                    <span className="text-amber-200">“{drift.visitLabel}”</span>.
                  </li>
                )}
              </ul>
            </div>
          </div>
          {canEdit && (
            <button type="button" onClick={beginEdit} className={`${btnGhost} mt-2`}>
              <Pencil className="w-3.5 h-3.5" />
              Review the checklist
            </button>
          )}
        </div>
      )}

      {/* Protocol status strip */}
      {protocol && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-300 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
          <FileText className="w-3.5 h-3.5 text-purple-300 shrink-0" />
          <span className="truncate max-w-[40%]" title={protocol.fileName}>
            {protocol.fileName || "Protocol"}
          </span>
          <StatusChip status={status} />
          {reading && (
            <span className="inline-flex items-center gap-1.5 text-slate-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              Reading the Schedule of Assessments — this takes a few minutes. You can leave and come back.
            </span>
          )}
          {failed && (
            <span className="inline-flex items-center gap-2 text-rose-200">
              {protocol.errorMessage || "The read failed."}
              {canEdit && (
                <button type="button" onClick={retry} className="underline hover:text-white inline-flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Retry
                </button>
              )}
            </span>
          )}
          {reviewHref && (
            <Link
              to={reviewHref}
              // Tell the review page where it was opened from, so its Back
              // returns here instead of stranding a study-page visitor in
              // SourceX.
              state={{ from: "study", studyId }}
              onClick={(e) => {
                if (dirty && !window.confirm("You have changes to the screening checklist that haven't been confirmed. Leave and lose them?")) {
                  e.preventDefault();
                }
              }}
              className="ml-auto inline-flex items-center gap-1 text-purple-300 hover:text-white"
            >
              Full SoA review <ExternalLink className="w-3 h-3" />
            </Link>
          )}
          {/* Who put this here, and when — its own quiet line under the badges.
              Provenance, read once when someone asks, never scanned. */}
          {uploadedBlurb && (
            <div className="basis-full text-[10px] text-slate-500">Uploaded {uploadedBlurb}</div>
          )}
        </div>
      )}

      {/* Confirmed list */}
      {showConfirmed && !confirmedItems.length && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-400/20 bg-emerald-500/[0.06] px-2.5 py-2 text-xs text-slate-100">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" />
          Confirmed — nothing needs to be done at the hospital.
        </div>
      )}
      {showConfirmed && confirmedItems.length > 0 && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {confirmedItems.map((i) => (
            <li
              key={i.id}
              className="flex items-start gap-2 rounded-lg border border-emerald-400/20 bg-emerald-500/[0.06] px-2.5 py-1.5"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-slate-100 leading-snug">{i.label}</div>
                {i.footnotes?.length > 0 && (
                  <div className="text-[10px] text-slate-400 leading-snug mt-0.5">{i.footnotes.join(" ")}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Nothing yet */}
      {!showConfirmed && !protocol && !draft && (
        <p className="text-xs text-slate-400">
          {canEdit
            ? "Upload the protocol PDF. RegX reads its Schedule of Assessments and lists what the screening visit requires as a checklist you confirm. Or build the checklist manually."
            : "No protocol has been uploaded for this study yet."}
        </p>
      )}

      {/* Draft checklist — from an extraction, or started by hand */}
      {!showConfirmed && ((protocol && readyForDraft) || draft) && (
        <div className="space-y-3">
          {draft?.guessedVisit && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-500/15 px-3 py-2 text-xs text-amber-200">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                No column in the schedule was labelled as screening, so this list is from the first visit column
                {draft.visitLabel ? ` (“${draft.visitLabel}”)` : ""}. Check it against the protocol.
              </span>
            </div>
          )}
          {draft?.visitLabel && !draft.guessedVisit && (
            <p className="text-[11px] text-slate-400">
              From the SoA column <span className="text-slate-200">“{draft.visitLabel}”</span>. Everything starts
              unticked — tick only what must be done at the hospital, rename as needed, add what the table left out,
              then confirm. Confirming with nothing ticked is fine.
            </p>
          )}

          {!draft?.items?.length && (protocol && extraction ? (
            <p className="text-xs text-slate-400">
              The schedule was read but no assessments were marked in the screening column. Add the requirements by hand below
              {reviewHref ? " or open the full SoA review" : ""}.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              Add each thing that must be done at the hospital, then confirm. Confirming with nothing added is fine.
            </p>
          ))}

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {(draft?.items || []).map((i) => (
              <li
                key={i.id}
                className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 transition ${
                  i.selected !== false ? "border-white/15 bg-white/[0.04]" : "border-white/5 bg-transparent opacity-60"
                }`}
              >
                <input
                  type="checkbox"
                  checked={i.selected !== false}
                  onChange={() => toggle(i.id)}
                  disabled={!canEdit}
                  className="mt-1 w-3.5 h-3.5 accent-purple-500 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  {canEdit ? (
                    <input
                      value={i.label}
                      onChange={(e) => relabel(i.id, e.target.value)}
                      className="w-full bg-transparent text-xs text-slate-100 focus:outline-none focus:border-b focus:border-purple-400/50"
                    />
                  ) : (
                    <div className="text-xs text-slate-100">{i.label}</div>
                  )}
                  {i.footnotes?.length > 0 && (
                    <div className="text-[10px] text-slate-400 leading-snug mt-0.5">{i.footnotes.join(" ")}</div>
                  )}
                  {i.source === "manual" && <div className="text-[9px] uppercase tracking-wider text-purple-300/80 mt-0.5">added by hand</div>}
                </div>
                {canEdit && i.source === "manual" && (
                  <button type="button" onClick={() => remove(i.id)} className="text-slate-500 hover:text-rose-300" title="Remove">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>

          {canEdit && (
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <div className="flex-1 flex gap-2">
                <input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addManual();
                    }
                  }}
                  placeholder="Add a requirement, e.g. Pregnancy test"
                  className={inputCls}
                />
                <button type="button" onClick={addManual} disabled={!newItem.trim()} className={btnGhost}>
                  <Plus className="w-3.5 h-3.5" />
                  Add
                </button>
              </div>
              <div className="flex gap-2 sm:ml-auto">
                {(confirmed || !protocol || failed) && (
                  <button type="button" onClick={cancelEdit} className={btnGhost} disabled={busy}>
                    Cancel
                  </button>
                )}
                <button type="button" onClick={confirm} disabled={busy} className={btnPrimary}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 relative" />}
                  <span className="relative">
                    {selectedCount
                      ? `Confirm ${selectedCount} item${selectedCount === 1 ? "" : "s"}`
                      : "Confirm — nothing at the hospital"}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {dialog}
      <NewVersionDialog
        open={!!askVersion}
        fileName={askVersion?.file?.name}
        current={askVersion?.current}
        next={askVersion?.next}
        isAdmin
        onYes={() => uploadProtocolFile(askVersion.file, true)}
        onNo={() => uploadProtocolFile(askVersion.file, false)}
        onCancel={() => setAskVersion(null)}
      />
    </StudyCard>
  );
};

const CHIP = {
  [PROTOCOL_STATUS.UPLOADED]: "bg-sky-400/15 text-sky-300 border-sky-400/30",
  [PROTOCOL_STATUS.PROCESSING]: "bg-sky-400/15 text-sky-300 border-sky-400/30",
  [PROTOCOL_STATUS.NEEDS_REVIEW]: "bg-amber-400/15 text-amber-300 border-amber-400/30",
  [PROTOCOL_STATUS.APPROVED]: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  [PROTOCOL_STATUS.FAILED]: "bg-rose-400/15 text-rose-300 border-rose-400/30",
};
const CHIP_TEXT = {
  [PROTOCOL_STATUS.UPLOADED]: "Queued",
  [PROTOCOL_STATUS.PROCESSING]: "Reading…",
  [PROTOCOL_STATUS.NEEDS_REVIEW]: "SoA read",
  [PROTOCOL_STATUS.APPROVED]: "SoA approved",
  [PROTOCOL_STATUS.FAILED]: "Failed",
};

const StatusChip = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${CHIP[status] || "bg-white/5 text-slate-300 border-white/10"}`}>
    {CHIP_TEXT[status] || status || "—"}
  </span>
);

export default ScreeningVisitCard;
